/*
|--------------------------------------------------------------------------
| Animated List
|--------------------------------------------------------------------------
|
| Controls staggered entrance and exit animations for list items.
|
*/

class AnimatedList extends HTMLUListElement {
  /*
    |--------------------------------------------------------------------------
    | Lifecycle
    |--------------------------------------------------------------------------
    |
    */

  connectedCallback () {
    this.classList.add('ani-AnimatedList')

    this.items = [...this.children].filter((item) => item.matches('li'))

    this.animationFinished = 0

    this.setupItems()

    this.addEventListeners()

    this.observeAttributes()

    this.handleVisibility(true)
  }

  disconnectedCallback () {
    this.observer?.disconnect()

    this.removeEventListeners()
  }

  /*
    |--------------------------------------------------------------------------
    | Setup
    |--------------------------------------------------------------------------
    |
    */

  setupItems () {
    const count = this.items.length

    this.items.forEach((item, index) => {
      item.classList.add('ani-AnimatedList_Item')

      item.style.setProperty('--ani-AnimatedList_ForwardIndex', index)

      item.style.setProperty(
        '--ani-AnimatedList_ReverseIndex',
        count - index - 1,
      )
    })
  }

  /*
    |--------------------------------------------------------------------------
    | Attribute observer
    |--------------------------------------------------------------------------
    |
    */

  observeAttributes () {
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'aria-hidden'
        ) {
          this.handleVisibility()
        }
      })
    })

    this.observer.observe(this, {
      attributes: true,
    })
  }

  /*
    |--------------------------------------------------------------------------
    | Visibility handling
    |--------------------------------------------------------------------------
    |
    */

  handleVisibility (initial = false) {
    const hidden = this.getAttribute('aria-hidden') === 'true'

    if (hidden) {
      this.close(initial)
    } else {
      this.open(initial)
    }
  }

  /*
    |--------------------------------------------------------------------------
    | Open
    |--------------------------------------------------------------------------
    |
    */

  open (initial = false) {
    if (
      this.dataset.aniState === 'open' ||
      this.dataset.aniState === 'opening'
    ) {
      return
    }

    this.cancelAnimation()

    this.enableInteraction()

    if (initial) {
      this.dataset.aniState = 'open'

      return
    }

    this.startAnimation('opening')
  }

  /*
    |--------------------------------------------------------------------------
    | Close
    |--------------------------------------------------------------------------
    |
    */

  close (initial = false) {
    if (
      this.dataset.aniState === 'closed' ||
      this.dataset.aniState === 'closing'
    ) {
      return
    }

    this.cancelAnimation()

    this.disableInteraction()

    if (initial) {
      this.dataset.aniState = 'closed'

      return
    }

    this.startAnimation('closing')
  }

  /*
    |--------------------------------------------------------------------------
    | Animation state
    |--------------------------------------------------------------------------
    |
    */

  startAnimation (state) {
    this.animationFinished = 0

    this.dataset.aniState = state
  }

  /*
    |--------------------------------------------------------------------------
    | Cancel animation
    |--------------------------------------------------------------------------
    |
    */

  cancelAnimation () {
    this.items.forEach((item) => {
      item.getAnimations().forEach((animation) => {
        animation.cancel()
      })
    })

    this.animationFinished = 0
  }

  /*
    |--------------------------------------------------------------------------
    | Interaction state
    |--------------------------------------------------------------------------
    |
    */

  enableInteraction () {
    this.removeAttribute('inert')
  }

  disableInteraction () {
    this.setAttribute('inert', '')
  }

  /*
    |--------------------------------------------------------------------------
    | Animation events
    |--------------------------------------------------------------------------
    |
    */

  addEventListeners () {
    this.items.forEach((item) => {
      item.addEventListener('animationend', this.handleAnimationEnd)

      item.addEventListener('animationcancel', this.handleAnimationCancel)
    })
  }

  removeEventListeners () {
    this.items.forEach((item) => {
      item.removeEventListener('animationend', this.handleAnimationEnd)

      item.removeEventListener('animationcancel', this.handleAnimationCancel)
    })
  }

  handleAnimationEnd = (event) => {
    if (!event.target.matches('.ani-AnimatedList_Item')) {
      return
    }

    this.animationFinished++

    if (this.animationFinished < this.items.length) {
      return
    }

    if (this.dataset.aniState === 'opening') {
      this.dataset.aniState = 'open'
    }

    if (this.dataset.aniState === 'closing') {
      this.dataset.aniState = 'closed'
    }
  }

  handleAnimationCancel = () => {
    this.animationFinished = 0
  }
}

if (!customElements.get('animated-list')) {
  customElements.define('animated-list', AnimatedList, {
    extends: 'ul',
  })
}

/*
|--------------------------------------------------------------------------
| Animated Nav
|--------------------------------------------------------------------------
|
| Controls staggered entrance and exit animations for Nav items.
|
*/

class AnimatedNav extends window.BAO.CustomElement() {
  /*
    |--------------------------------------------------------------------------
    | Lifecycle
    |--------------------------------------------------------------------------
    |
    */

  connectedCallback () {
    this.items = this.els.item.elements
    this.list = this.closest('[data-animated-nav-external-el="list"]')

    this.animationFinished = 0

    this.setupItems()

    this.addEventListeners()

    this.observeAttributes()

    this.handleVisibility(true)
  }

  disconnectedCallback () {
    this.observer?.disconnect()

    this.removeEventListeners()
  }

  /*
    |--------------------------------------------------------------------------
    | Setup
    |--------------------------------------------------------------------------
    |
    */

  setupItems () {
    const count = this.items.length

    this.items.forEach((item, index) => {
      item.classList.add('ani-AnimatedList_Item')

      item.style.setProperty('--ani-AnimatedList_ForwardIndex', index)

      item.style.setProperty(
        '--ani-AnimatedList_ReverseIndex',
        count - index - 1,
      )
    })
  }

  /*
    |--------------------------------------------------------------------------
    | Attribute observer
    |--------------------------------------------------------------------------
    |
    */

  observeAttributes () {
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'aria-hidden'
        ) {
          this.handleVisibility()
        }
      })
    })

    this.observer.observe(this.list, {
      attributes: true,
    })
  }

  /*
    |--------------------------------------------------------------------------
    | Visibility handling
    |--------------------------------------------------------------------------
    |
    */

  handleVisibility (initial = false) {
    const hidden = this.list.getAttribute('aria-hidden') === 'true'

    if (initial) {
      this.close(initial)
      return
    }

    if (hidden) {
      this.close(initial)
    } else {
      this.open(initial)
    }
  }

  /*
    |--------------------------------------------------------------------------
    | Open
    |--------------------------------------------------------------------------
    |
    */

  open (initial = false) {
    if (
      this.dataset.aniState === 'open' ||
      this.dataset.aniState === 'opening'
    ) {
      return
    }

    this.cancelAnimation()

    this.enableInteraction()

    if (initial) {
      this.dataset.aniState = 'open'

      return
    }

    this.startAnimation('opening')
  }

  /*
    |--------------------------------------------------------------------------
    | Close
    |--------------------------------------------------------------------------
    |
    */

  close (initial = false) {
    if (
      this.dataset.aniState === 'closed' ||
      this.dataset.aniState === 'closing'
    ) {
      return
    }

    this.cancelAnimation()

    this.disableInteraction()

    if (initial) {
      this.dataset.aniState = 'closed'

      return
    }

    this.startAnimation('closing')
  }

  /*
    |--------------------------------------------------------------------------
    | Animation state
    |--------------------------------------------------------------------------
    |
    */

  startAnimation (state) {
    this.animationFinished = 0

    this.dataset.aniState = state
  }

  /*
    |--------------------------------------------------------------------------
    | Cancel animation
    |--------------------------------------------------------------------------
    |
    */

  cancelAnimation () {
    this.items.forEach((item) => {
      item.getAnimations().forEach((animation) => {
        animation.cancel()
      })
    })

    this.animationFinished = 0
  }

  /*
    |--------------------------------------------------------------------------
    | Interaction state
    |--------------------------------------------------------------------------
    |
    */

  enableInteraction () {
    this.removeAttribute('inert')
  }

  disableInteraction () {
    this.setAttribute('inert', '')
  }

  /*
    |--------------------------------------------------------------------------
    | Animation events
    |--------------------------------------------------------------------------
    |
    */

  addEventListeners () {
    this.items.forEach((item) => {
      item.addEventListener('animationend', this.handleAnimationEnd)

      item.addEventListener('animationcancel', this.handleAnimationCancel)
    })
  }

  removeEventListeners () {
    this.items.forEach((item) => {
      item.removeEventListener('animationend', this.handleAnimationEnd)

      item.removeEventListener('animationcancel', this.handleAnimationCancel)
    })
  }

  handleAnimationEnd = (event) => {
    if (!event.target.matches('.ani-AnimatedList_Item')) {
      return
    }

    this.animationFinished++

    if (this.animationFinished < this.items.length) {
      return
    }

    if (this.dataset.aniState === 'opening') {
      this.dataset.aniState = 'open'
    }

    if (this.dataset.aniState === 'closing') {
      this.dataset.aniState = 'closed'
    }
  }

  handleAnimationCancel = () => {
    this.animationFinished = 0
  }
}

if (!customElements.get('animated-nav')) {
  customElements.define('animated-nav', AnimatedNav)
}
